import { useState } from "react"
import { toast } from "react-toastify"
import { Badge, Button, Card, CardBody, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table } from "reactstrap"

import InputField from "components/Common/InputField"
import PageWrapper from "components/Common/PageWrapper"
import { ACCESS } from "constants/accessPermissions"
import { useCancelLeaveRequest, useCreateLeaveRequest, useLeaveTypes, useMyBalances, useMyLeaveRequests } from "hooks/leaveHook"

const STATUS_COLORS = { pending: "warning", approved: "success", rejected: "danger", cancelled: "secondary" }

const MyLeave = () => {
  document.title = "My Leave"

  const [statusFilter, setStatusFilter] = useState("")
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState({ leave_type_id: "", start_date: "", end_date: "", is_half_day: false, reason: "" })

  const { data: balancesData, refetch: refetchBalances } = useMyBalances({})
  const { data: requestsData, refetch: refetchRequests } = useMyLeaveRequests({ ...(statusFilter && { status: statusFilter }) })
  const { data: typesData } = useLeaveTypes({})
  const createMutation = useCreateLeaveRequest()
  const cancelMutation = useCancelLeaveRequest()

  const balances = balancesData?.data?.data ?? balancesData?.data ?? []
  const requests = requestsData?.data?.data ?? requestsData?.data ?? []
  const leaveTypes = typesData?.data?.data ?? typesData?.data ?? []

  const handleSubmit = async () => {
    if (!form.leave_type_id || !form.start_date || !form.end_date) {
      toast.error("Please fill all required fields")
      return
    }
    try {
      await createMutation.mutateAsync(form)
      toast.success("Leave request submitted")
      setModal(false)
      setForm({ leave_type_id: "", start_date: "", end_date: "", is_half_day: false, reason: "" })
      refetchBalances()
      refetchRequests()
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to submit request")
    }
  }

  const handleCancel = async id => {
    try {
      await cancelMutation.mutateAsync(id)
      toast.success("Leave request cancelled")
      refetchBalances()
      refetchRequests()
    } catch (err) {
      toast.error(err?.response?.data?.message || "Cancel failed")
    }
  }

  return (
    <PageWrapper title="My Leave">
      <Card className="mb-3">
        <CardBody>
          <Row>
            {balances.map(b => (
              <Col key={b.id} md={3} className="mb-2">
                <div className="border rounded p-3">
                  <div className="text-muted small">{b.leave_type?.name}</div>
                  <div className="fs-4 fw-bold">{b.remaining_days}</div>
                  <div className="text-muted small">of {b.allocated_days} days</div>
                </div>
              </Col>
            ))}
          </Row>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <Row className="mb-3">
            <Col md={3}>
              <select className="form-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </Col>
            <Col className="text-end">
              <Button color="primary" onClick={() => setModal(true)}>
                Request Leave
              </Button>
            </Col>
          </Row>

          <Table responsive>
            <thead>
              <tr>
                <th>Leave Type</th>
                <th>Start</th>
                <th>End</th>
                <th>Days</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {requests.map(r => (
                <tr key={r.id}>
                  <td>{r.leave_type?.name}</td>
                  <td>{r.start_date}</td>
                  <td>{r.end_date}</td>
                  <td>{r.days}{r.is_half_day ? " (½)" : ""}</td>
                  <td><Badge color={STATUS_COLORS[r.status]}>{r.status}</Badge></td>
                  <td>
                    {(r.status === "pending" || r.status === "approved") && (
                      <Button size="sm" color="danger" onClick={() => handleCancel(r.id)} disabled={cancelMutation.isPending}>
                        Cancel
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {requests.length === 0 && (
                <tr><td colSpan={6} className="text-center text-muted py-4">No leave requests found.</td></tr>
              )}
            </tbody>
          </Table>
        </CardBody>
      </Card>

      <Modal isOpen={modal} toggle={() => setModal(false)}>
        <ModalHeader>Request Leave</ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <label className="form-label">Leave Type</label>
            <select
              className="form-select"
              value={form.leave_type_id}
              onChange={e => setForm(f => ({ ...f, leave_type_id: e.target.value }))}
            >
              <option value="">Select type</option>
              {leaveTypes.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div className="row mb-3">
            <div className="col">
              <InputField label="Start Date" type="date" value={form.start_date} onChange={v => setForm(f => ({ ...f, start_date: v }))} />
            </div>
            <div className="col">
              <InputField label="End Date" type="date" value={form.end_date} onChange={v => setForm(f => ({ ...f, end_date: v }))} />
            </div>
          </div>
          <div className="form-check mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="half_day"
              checked={form.is_half_day}
              onChange={e => setForm(f => ({ ...f, is_half_day: e.target.checked }))}
            />
            <label className="form-check-label" htmlFor="half_day">Half Day Leave</label>
          </div>
          <InputField label="Reason (optional)" type="textarea" value={form.reason} onChange={v => setForm(f => ({ ...f, reason: v }))} rows={2} />
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSubmit} disabled={createMutation.isPending}>
            Submit
          </Button>
          <Button color="secondary" onClick={() => setModal(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </PageWrapper>
  )
}

export default MyLeave